import _ from 'lodash';

import { ModeOfTransport } from '@zen/Booking/types';
import { getLegacyCargoItemType } from '@zen/Cargo/cargoDictionaryMapping.helper';
import CargoOptions from '@zen/Cargo/components/CargoOptions';
import CargoValue from '@zen/Cargo/components/CargoValue';
import { InlineFieldWithSuggestions } from '@zen/Components/Suggestions/InlineFieldWithSuggestions/InlineFieldWithSuggestions';
import type { Suggestion } from '@zen/Components/Suggestions/types';
import { type CustomField, SuggestionAttribute } from '@zen/graphql/types.generated';
import { formatNumber } from '@zen/utils/formatting';
import type { Nullable, Optional } from '@zen/utils/typescript';

import CargoDescriptionInlineField from './forms/CargoDescriptionInlineField';
import HsCodesInlineField from './forms/HsCodesInlineField';
import MarksAndNumbersInlineField from './forms/MarksAndNumbersInlineField';
import PackagingTypesInlineField from './forms/PackagingTypeInlineField/PackagingTypeInlineField';
import { cargoLabelMapping, formatDimensions, formatWeight } from './helpers';
import type {
  CargoDetailsFieldsConfig,
  CargoFieldConfig,
  CargoFieldName,
  CargoModeEnum,
  CoreCargo,
  CoreCargoExtended,
  Money,
  RiskLevelsEnum
} from './types';
import { CargoItemTypeEnum, coreCargoTypeLabelMapping } from './types';

type Summary = { highestRiskLevel?: Nullable<RiskLevelsEnum>; totalValueOfGoods?: Nullable<Money[]> };

const formatLooseCargo = (looseCargo: Optional<CoreCargoExtended>): Nullable<string> => {
  if (!looseCargo?.cargoType) {
    return null;
  }

  const { quantity, cargoType } = looseCargo;
  const cargoTypeLabel: string = coreCargoTypeLabelMapping[cargoType];

  return quantity ? `${quantity} x ${cargoTypeLabel}` : cargoTypeLabel;
};

const extractCustomProperty = (propertyName: string, customFields?: Nullable<CustomField[]>): string => {
  return customFields?.find((cf) => cf.name === propertyName)?.value ?? '';
};

const cargoTypeFieldsMapping: Record<CargoItemTypeEnum, CargoFieldName[]> = {
  [CargoItemTypeEnum.CONTAINER]: [
    'containerNumber',
    'cargoType',
    'looseCargo',
    'packagingTypes',
    'description',
    'hsCodes',
    'marksAndNumbers',
    'grossWeight',
    'volume',
    'containerSealNumber',
    'dryStandard',
    'flatRack',
    'garmentsOnHangers',
    'hazardous',
    'openTop',
    'overweight',
    'refrigerated'
  ],
  [CargoItemTypeEnum.LOOSE_CARGO]: [
    'cargoType',
    'dimensions',
    'grossWeight',
    'volume',
    'chargeableWeight',
    'containerNumber',
    'hazardous',
    'refrigerated',
    'stackable',
    'vehiclePlateNumber',
    'trailerId',
    'driverDetails'
  ],
  [CargoItemTypeEnum.VEHICLE]: [
    'cargoType',
    'looseCargo',
    'dimensions',
    'valueOfCargo',
    'grossWeight',
    'volume',
    'vehiclePlateNumber',
    'trailerId',
    'driverDetails',
    'hazardous',
    'healthCertificate',
    'refrigerated',
    'tailLift'
  ]
};

const getFieldsConfig = (
  cargo: CoreCargo,
  cargoMode: Optional<CargoModeEnum>,
  cargoSummary: Optional<Summary>,
  cargoSuggestions: Suggestion[]
): CargoFieldConfig => {
  const {
    chargeableWeight,
    collection,
    containerNumber,
    containerSealNumber,
    customFields,
    dimensions,
    delivery,
    dryStandard,
    flatRack,
    garmentsOnHangers,
    grossWeight,
    hazardous,
    healthCertificate,
    openTop,
    overweight,
    permissions,
    refrigerated,
    stackable,
    tailLift,
    trailerId,
    volume
  } = cargo;

  return {
    cargoType: {
      label: cargoMode ? cargoLabelMapping[cargoMode] : 'Cargo',
      value: getCargoQuantityAndLabel(cargo)
    },
    chargeableWeight: {
      label: 'Chargeable weight',
      value: formatWeight(chargeableWeight)
    },
    containerNumber: {
      label: 'Container number',
      value: (
        <InlineFieldWithSuggestions
          attribute={SuggestionAttribute.CARGO_CONTAINER_NUMBER}
          inlineFieldNode={containerNumber}
          originalValuePresent={!_.isEmpty(containerNumber)}
          refetchQueries={['cargoOverview']}
          suggestions={cargoSuggestions}
        />
      )
    },
    containerSealNumber: {
      label: 'Seal number',
      value: (
        <InlineFieldWithSuggestions
          attribute={SuggestionAttribute.CARGO_SEAL_NUMBER}
          inlineFieldNode={containerSealNumber}
          originalValuePresent={!_.isEmpty(containerSealNumber)}
          refetchQueries={['cargoOverview']}
          suggestions={cargoSuggestions}
        />
      )
    },
    collectionReference: {
      label: 'Collection reference',
      value: collection?.reference
    },
    description: {
      label: null,
      value: (
        <InlineFieldWithSuggestions
          attribute={SuggestionAttribute.CARGO_DESCRIPTION}
          inlineFieldNode={
            <CargoDescriptionInlineField
              canUpdateCargoDescription={!!permissions?.canUpdateDescription?.value}
              cargoDescription={extractCustomProperty('cargoDescription', customFields)}
              cargoId={cargo.id}
            />
          }
          originalValuePresent={!_.isEmpty(extractCustomProperty('cargoDescription', customFields))}
          refetchQueries={['cargoOverview']}
          suggestions={cargoSuggestions}
        />
      )
    },
    dimensions: {
      label: 'Loose cargo dimensions',
      value: formatDimensions(dimensions)
    },
    deliveryReference: {
      label: 'Delivery reference',
      value: delivery?.reference
    },
    driverDetails: {
      label: 'Driver details',
      value: collection?.driverDetails
    },
    dryStandard: {
      label: null,
      value: dryStandard && <CargoOptions dryStandard={dryStandard} />
    },
    flatRack: {
      label: null,
      value: flatRack && <CargoOptions flatRack={flatRack} />
    },
    garmentsOnHangers: {
      label: null,
      value: garmentsOnHangers && <CargoOptions garmentsOnHangers={garmentsOnHangers} />
    },
    grossWeight: {
      label: 'Gross weight',
      value: (
        <InlineFieldWithSuggestions
          attribute={SuggestionAttribute.CARGO_GROSS_WEIGHT}
          inlineFieldNode={grossWeight && formatWeight(grossWeight)}
          originalValuePresent={!_.isEmpty(grossWeight)}
          refetchQueries={['cargoOverview']}
          suggestions={cargoSuggestions}
        />
      )
    },
    hazardous: {
      label: null,
      value: hazardous && <CargoOptions hazardous={hazardous} />
    },
    healthCertificate: {
      label: null,
      value: healthCertificate && <CargoOptions healthCertificate={healthCertificate} />
    },
    hsCodes: {
      label: null,
      value: (
        <InlineFieldWithSuggestions
          attribute={SuggestionAttribute.CARGO_TARIFF_CODES}
          inlineFieldNode={
            <HsCodesInlineField
              canUpdateHsCodes={!!permissions?.canUpdateHsCodes?.value}
              cargoId={cargo.id}
              hsCodes={extractCustomProperty('hsCodes', customFields)}
            />
          }
          originalValuePresent={!_.isEmpty(extractCustomProperty('hsCodes', customFields))}
          refetchQueries={['cargoOverview']}
          suggestions={cargoSuggestions}
        />
      )
    },
    looseCargo: {
      label: 'Loose cargo quantity & type',
      value: formatLooseCargo(cargo.looseCargo)
    },
    marksAndNumbers: {
      label: null,
      value: (
        <InlineFieldWithSuggestions
          attribute={SuggestionAttribute.CARGO_MARKS_AND_NUMBERS}
          inlineFieldNode={
            <MarksAndNumbersInlineField
              canUpdateMarksAndNumbers={!!permissions?.canUpdateMarksAndNumbers?.value}
              cargoId={cargo.id}
              marksAndNumbers={extractCustomProperty('marksAndNumbers', customFields)}
            />
          }
          originalValuePresent={!_.isEmpty(extractCustomProperty('marksAndNumbers', customFields))}
          refetchQueries={['cargoOverview']}
          suggestions={cargoSuggestions}
        />
      )
    },
    openTop: {
      label: null,
      value: openTop && <CargoOptions openTop={openTop} />
    },
    overweight: {
      label: null,
      value: overweight && <CargoOptions overweight={overweight} />
    },
    packagingTypes: {
      label: null,
      value: (
        <PackagingTypesInlineField
          canUpdatePackagingTypes={!!permissions?.canUpdatePackagingTypes?.value}
          cargoId={cargo.id}
          packagingTypes={extractCustomProperty('packagingTypes', customFields)}
        />
      )
    },
    refrigerated: {
      label: null,
      value: refrigerated && <CargoOptions refrigerated={refrigerated} />
    },
    stackable: {
      label: null,
      value: stackable && <CargoOptions stackable={stackable} />
    },
    tailLift: {
      label: null,
      value: tailLift && <CargoOptions tailLift={tailLift} />
    },
    trailerId: {
      label: 'Trailer ID',
      value: trailerId
    },
    valueOfCargo: {
      label: 'Value of cargo',
      value: <CargoValue highestRiskLevel={cargoSummary?.highestRiskLevel} totalValueOfGoods={cargoSummary?.totalValueOfGoods} />
    },
    vehiclePlateNumber: {
      label: 'Vehicle plate number',
      value: collection?.vehiclePlateNumber
    },
    volume: {
      label: 'CBM',
      value: (
        <InlineFieldWithSuggestions
          attribute={SuggestionAttribute.CARGO_VOLUME}
          inlineFieldNode={volume?.value && formatNumber(volume?.value)}
          originalValuePresent={!_.isEmpty(volume)}
          refetchQueries={['cargoOverview']}
          suggestions={cargoSuggestions}
        />
      )
    }
  };
};

const filterFieldsByModeOfTransport = (fields: CargoFieldName[], modeOfTransport: ModeOfTransport) => {
  const fieldsAvailableOnModeOfTransports = {
    chargeableWeight: [ModeOfTransport.AIR],
    containerNumber: [ModeOfTransport.OCEAN],
    vehiclePlateNumber: [ModeOfTransport.TRUCK],
    trailerId: [ModeOfTransport.TRUCK],
    driverDetails: [ModeOfTransport.TRUCK]
  };

  return fields.filter((field) => {
    const modeOfTransports: ModeOfTransport[] | undefined =
      fieldsAvailableOnModeOfTransports[field as keyof typeof fieldsAvailableOnModeOfTransports];

    if (!modeOfTransports) {
      return true;
    }

    return modeOfTransports.includes(modeOfTransport);
  });
};

export const getCargoDetails = (
  cargo: CoreCargo,
  cargoMode: Optional<CargoModeEnum>,
  cargoSummary: Optional<Summary>,
  modeOfTransport: Optional<ModeOfTransport>,
  fieldConfig: Partial<CargoDetailsFieldsConfig> = {},
  cargoSuggestions: Suggestion[] = []
) => {
  if (!modeOfTransport) {
    return [];
  }

  const cargoItemType = getLegacyCargoItemType(cargo?.cargoType);

  const cargoDetailsFieldsConfig: Record<CargoItemTypeEnum, CargoFieldName[]> = {
    ...cargoTypeFieldsMapping,
    ...(fieldConfig as Record<CargoItemTypeEnum, CargoFieldName[]>)
  };

  const fields: CargoFieldName[] = cargoItemType ? cargoDetailsFieldsConfig[cargoItemType] : [];

  const config: CargoFieldConfig = getFieldsConfig(cargo, cargoMode, cargoSummary, cargoSuggestions);
  const availableFields: CargoFieldName[] =
    cargoItemType === CargoItemTypeEnum.LOOSE_CARGO ? filterFieldsByModeOfTransport(fields, modeOfTransport) : fields;

  return availableFields.map((field: CargoFieldName) => config[field]);
};

export const getCargoQuantityAndLabel = ({ cargoType, quantity }: CoreCargo): string => {
  const cargoTypeLabel: string = cargoType ? coreCargoTypeLabelMapping[cargoType] : '';
  const legacyCargoType: Optional<CargoItemTypeEnum> = getLegacyCargoItemType(cargoType);
  const isContainerOrVehicleType: boolean =
    legacyCargoType === CargoItemTypeEnum.CONTAINER || legacyCargoType === CargoItemTypeEnum.VEHICLE;

  if (!quantity || isContainerOrVehicleType) {
    return cargoTypeLabel;
  }

  return `${quantity} x ${cargoTypeLabel}`;
};
